import api from "./apiClient";

const wait = (ms = 500) => new Promise((resolve) => setTimeout(resolve, ms));

// داده‌های کمکی (Fallback Mocks) در صورت نیاز فرانت‌اند
const MOCK_WALLET = {
  BALANCE: "1250000",
  CODE: "USD",
  IS_ACTIVE: "true"
};

const MOCK_COUPONS = {
  SAVE10: { type: "percent", value: 10, message: "10% discount applied." },
  SAVE50: { type: "fixed", value: 50000, message: "50,000 discount applied." },
  VIP20: { type: "percent", value: 20, message: "20% discount applied." },
};

function normalizeAmount(value) {
  const n = Number(value ?? 0);
  return Number.isFinite(n) ? n : 0;
}

export async function getWalletBalance() {
  try {
    const response = await api.post("/DS/admin_center/get_wallet_balance.zjs");
    return response.data;
  } catch (error) {
    console.warn("Real Wallet API failed, using fallback mock", error);
    return [MOCK_WALLET];
  }
}

export async function getPendingPayments({ paymentRef } = {}) {
  try {
    const response = await api.post("/DS/admin_center/get_pending_invoices.zjs");
    return response.data;
  } catch (error) {
    console.warn("Real Pending Payments API failed, returning empty list", error);
    return [];
  }
}

/**
 * ۳. اعمال کد تخفیف
 */
export async function applyCouponCode({ code, paymentRef } = {}) {
  await wait(450);

  const cleanCode = String(code || "").trim().toUpperCase();
  const coupon = MOCK_COUPONS[cleanCode];

  if (!coupon) {
    throw new Error("Invalid or expired coupon code.");
  }

  // مبالغ فرضی برای اعمال تخفیف در تست
  const subtotal = paymentRef ? 780000 : 1120000;
  const discountAmount =
    coupon.type === "percent"
      ? Math.floor((subtotal * coupon.value) / 100)
      : normalizeAmount(coupon.value);

  return {
    status: "SUCCESS",
    code: cleanCode,
    type: coupon.type,
    amount: discountAmount,
    message: coupon.message,
    subtotal,
    total: Math.max(0, subtotal - discountAmount),
  };
}

/**
 * ۴. ایجاد تراکنش پرداخت خارجی (درگاه کارت، کریپتو، پی‌پال و غیره)
 */
export async function createWalletCharge({
  paymentRef,
  amount,
  method,
  couponCode,
} = {}) {
  await wait(700);
  const normalizedAmount = normalizeAmount(amount);

  if (normalizedAmount <= 0) {
    throw new Error("Amount must be greater than zero.");
  }

  const supportedMethods = ["visa", "mastercard", "crypto", "paypal", "wallet"];
  if (!supportedMethods.includes(method)) {
    throw new Error("Unsupported payment method.");
  }

  // لینک‌های شبیه‌سازی‌شده درگاه‌های پرداخت خارجی
  const redirectMap = {
    visa: "https://example.com/mock-payment/visa",
    mastercard: "https://example.com/mock-payment/mastercard",
    crypto: "https://example.com/mock-payment/crypto",
    paypal: "https://example.com/mock-payment/paypal",
    wallet: null,
  };

  return {
    status: "SUCCESS",
    paymentRef: paymentRef || `PAY-${Date.now()}`,
    method,
    amount: normalizedAmount,
    couponCode: couponCode || null,
    redirectUrl: redirectMap[method],
    transactionId: `TX-${Math.random().toString(36).slice(2, 10).toUpperCase()}`,
  };
}

/**
 * ۵. پرداخت مستقیم از موجودی کیف پول
 */
export async function payFromWallet({
  paymentRef,
  amount,
  couponCode,
} = {}) {
  await wait(600);
  const normalizedAmount = normalizeAmount(amount);

  if (normalizedAmount <= 0) {
    throw new Error("Amount must be greater than zero.");
  }

  const currentBalance = Number(MOCK_WALLET.BALANCE);
  if (currentBalance < normalizedAmount) {
    throw new Error("Insufficient wallet balance.");
  }

  // شبیه‌سازی کسر موجودی در Mock
  MOCK_WALLET.BALANCE = String(currentBalance - normalizedAmount);

  return {
    status: "SUCCESS",
    paymentRef: paymentRef || `WALLET-${Date.now()}`,
    amount: normalizedAmount,
    couponCode: couponCode || null,
    remainingBalance: Number(MOCK_WALLET.BALANCE),
    transactionId: `WLT-${Math.random().toString(36).slice(2, 10).toUpperCase()}`,
  };
}
export async function createPayment({
  invoiceId,
  paymentMethod,          // 'wallet' | 'online' | 'split'
  enumPaymentGatewayId,   // 'visa' | 'mastercard' | 'crypto' | 'paypal'
  transactionId = "",
  paymentResponse = {},
  walletAmount = 0,
  gatewayAmount = 0
} = {}) {
  try {
    const response = await api.post("/DS/admin_center/create_payment.zjs", {
      invoice_id: invoiceId,
      payment_method: paymentMethod,
      enum_payment_gateway_id: enumPaymentGatewayId,
      transaction_id: transactionId || `TX-${Date.now()}`,
      payment_response: JSON.stringify(paymentResponse),
      wallet_amount: String(walletAmount),
      gateway_amount: String(gatewayAmount)
    });

    return response.data;
  } catch (error) {
    console.warn("Real create_payment API failed, executing mock payment action", error);
    await wait(800);

    // شبیه‌سازی موفقیت در حالت Mock برای فرانت‌اند
    const redirectMap = {
      visa: "https://example.com/mock-payment/visa",
      mastercard: "https://example.com/mock-payment/mastercard",
      crypto: "https://example.com/mock-payment/crypto",
      paypal: "https://example.com/mock-payment/paypal",
    };

    return {
      status: "SUCCESS",
      invoice_id: invoiceId,
      payment_method: paymentMethod,
      redirectUrl: paymentMethod !== "wallet" ? redirectMap[enumPaymentGatewayId] : null,
      transaction_id: transactionId || `TX-${Math.random().toString(36).slice(2, 10).toUpperCase()}`,
      message: "Payment initiated successfully."
    };
  }
}